import React, { useState, useRef, useEffect } from 'react';
import { getFeaturedCards } from '../services/pokemonApi';
import {
  getFeaturedOnePieceCards,
  getFeaturedDragonBallCards,
  getFeaturedDigimonCards,
  getCardImageUrl,
  getCardName,
  getCardRarity
} from '../services/tcgApi';
import '../styles/CardGallery.css';

const Card = ({ card, gameType }) => {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [glarePosition, setGlarePosition] = useState({ x: 50, y: 50 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateX = (mouseY / (rect.height / 2)) * -15;
    const rotateY = (mouseX / (rect.width / 2)) * 15;

    const glareX = ((e.clientX - rect.left) / rect.width) * 100;
    const glareY = ((e.clientY - rect.top) / rect.height) * 100;

    setTransform({ rotateX, rotateY });
    setGlarePosition({ x: glareX, y: glareY });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setTransform({ rotateX: 0, rotateY: 0 });
    setGlarePosition({ x: 50, y: 50 });
  };

  // Get image URL based on game type
  const getImageUrl = () => {
    if (gameType === 'pokemon') {
      return card.images?.large || card.images?.small;
    }
    return getCardImageUrl(card, gameType);
  };

  // Get card display name
  const getDisplayName = () => {
    if (gameType === 'pokemon') return card.name;
    return getCardName(card);
  };

  // Get card set/series name
  const getSetName = () => {
    if (gameType === 'pokemon') return card.set?.name || 'Unknown Set';
    // Handle set as object or string
    if (card.set) {
      return typeof card.set === 'object' ? card.set.name : card.set;
    }
    return card.series || card.cardSet || 'Unknown Set';
  };

  // Get rarity
  const getRarity = () => {
    if (gameType === 'pokemon') return card.rarity || 'Common';
    return getCardRarity(card);
  };

  const imageUrl = getImageUrl();
  const hasImage = imageUrl && !imageError;

  // Get game-specific gradient colors
  const getGameGradient = () => {
    const gradients = {
      pokemon: 'linear-gradient(135deg, #ffcb05 0%, #3d7dca 100%)',
      'one-piece': 'linear-gradient(135deg, #c41e3a 0%, #1a1a2e 100%)',
      'dragon-ball': 'linear-gradient(135deg, #ff6b00 0%, #ffd700 100%)',
      digimon: 'linear-gradient(135deg, #1e90ff 0%, #00bfff 100%)',
      'star-wars': 'linear-gradient(135deg, #1a1a1a 0%, #ffe81f 100%)',
      magic: 'linear-gradient(135deg, #8b4513 0%, #d4af37 100%)',
      gundam: 'linear-gradient(135deg, #e60012 0%, #ffffff 100%)'
    };
    return gradients[gameType] || gradients.pokemon;
  };

  return (
    <div
      ref={cardRef}
      className={`gallery-card ${gameType} ${isHovered ? 'hovered' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) ${isHovered ? 'scale(1.05)' : 'scale(1)'}`,
      }}
    >
      <div className="card-frame">
        {/* Holographic Effect */}
        <div
          className="card-holographic"
          style={{
            background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%,
              rgba(255,255,255,0.8) 0%,
              rgba(255,200,200,0.4) 20%,
              rgba(255,100,100,0.2) 40%,
              transparent 60%)`,
            opacity: isHovered ? 1 : 0,
          }}
        />

        {/* Rainbow Shine */}
        <div
          className="card-rainbow"
          style={{
            background: `linear-gradient(
              ${135 + transform.rotateY}deg,
              rgba(255,0,0,0.3) 0%,
              rgba(255,127,0,0.3) 14%,
              rgba(255,255,0,0.3) 28%,
              rgba(0,255,0,0.3) 42%,
              rgba(0,0,255,0.3) 57%,
              rgba(75,0,130,0.3) 71%,
              rgba(148,0,211,0.3) 85%,
              rgba(255,0,0,0.3) 100%
            )`,
            opacity: isHovered ? 0.6 : 0,
          }}
        />

        {/* Card Image */}
        {hasImage ? (
          <div className="card-image-wrapper">
            {!imageLoaded && (
              <div className="card-loading-placeholder" style={{ background: getGameGradient() }}>
                <div className="loading-spinner-small"></div>
              </div>
            )}
            <img
              src={imageUrl}
              alt={getDisplayName()}
              className={`card-real-image ${imageLoaded ? 'loaded' : ''}`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div className="card-content placeholder-card" style={{ background: getGameGradient() }}>
            <div className="card-header">
              <span className="card-name">{getDisplayName()}</span>
            </div>
            <div className="card-image-area">
              <div className="card-silhouette-placeholder">
                <span className="silhouette-icon">{gameType.charAt(0).toUpperCase()}</span>
              </div>
            </div>
            <div className="card-info">
              <span className="card-element">{gameType.toUpperCase()}</span>
              <span className="card-rarity">{getRarity()}</span>
            </div>
          </div>
        )}

        {/* Sparkle Effects */}
        <div className="card-sparkles">
          <span className="sparkle s1"></span>
          <span className="sparkle s2"></span>
          <span className="sparkle s3"></span>
          <span className="sparkle s4"></span>
        </div>

        {/* Border Glow */}
        <div className="card-border-glow"></div>
      </div>

      {/* Card Info Overlay */}
      <div className="card-info-overlay">
        <span className="overlay-name">{getDisplayName()}</span>
        <span className="overlay-set">{getSetName()}</span>
        <span className="overlay-rarity">{getRarity()}</span>
        <span className={`overlay-game game-${gameType}`}>{gameType.replace('-', ' ').toUpperCase()}</span>
      </div>

      {/* Action Burst on Hover */}
      <div className="card-action-burst">
        <span className="burst-text">WOW!</span>
      </div>
    </div>
  );
};

const CardGallery = () => {
  const [filter, setFilter] = useState('all');
  const [hoveredFilter, setHoveredFilter] = useState(null);
  const [pokemonCards, setPokemonCards] = useState([]);
  const [onePieceCards, setOnePieceCards] = useState([]);
  const [dragonBallCards, setDragonBallCards] = useState([]);
  const [digimonCards, setDigimonCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllCards = async () => {
      try {
        setLoading(true);

        // Fetch cards from all APIs in parallel
        const [pokemon, onePiece, dragonBall, digimon] = await Promise.allSettled([
          getFeaturedCards(),
          getFeaturedOnePieceCards(),
          getFeaturedDragonBallCards(),
          getFeaturedDigimonCards()
        ]);

        // Set cards even if some APIs fail
        if (pokemon.status === 'fulfilled') setPokemonCards(pokemon.value || []);
        if (onePiece.status === 'fulfilled') setOnePieceCards(onePiece.value || []);
        if (dragonBall.status === 'fulfilled') setDragonBallCards(dragonBall.value || []);
        if (digimon.status === 'fulfilled') setDigimonCards(digimon.value || []);

        setError(null);
      } catch (err) {
        setError('Failed to load some cards');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllCards();
  }, []);

  const filterOptions = [
    { id: 'all', label: 'ALL CARDS' },
    { id: 'pokemon', label: 'POKEMON' },
    { id: 'one-piece', label: 'ONE PIECE' },
    { id: 'dragon-ball', label: 'DRAGON BALL' },
    { id: 'digimon', label: 'DIGIMON' }
  ];

  const getFilteredCards = () => {
    switch (filter) {
      case 'pokemon':
        return { pokemon: pokemonCards, onePiece: [], dragonBall: [], digimon: [] };
      case 'one-piece':
        return { pokemon: [], onePiece: onePieceCards, dragonBall: [], digimon: [] };
      case 'dragon-ball':
        return { pokemon: [], onePiece: [], dragonBall: dragonBallCards, digimon: [] };
      case 'digimon':
        return { pokemon: [], onePiece: [], dragonBall: [], digimon: digimonCards };
      default:
        return {
          pokemon: pokemonCards,
          onePiece: onePieceCards,
          dragonBall: dragonBallCards,
          digimon: digimonCards
        };
    }
  };

  const { pokemon, onePiece, dragonBall, digimon } = getFilteredCards();
  const totalCards = pokemon.length + onePiece.length + dragonBall.length + digimon.length;

  return (
    <section id="cards" className="card-gallery">
      <div className="section-header">
        <div className="comic-panel-border">
          <h2 className="section-title">
            <span className="title-text">SUPPORTED CARDS</span>
            <span className="title-burst">!</span>
          </h2>
          <p className="section-subtitle">
            Explore cards from Pokemon, One Piece, Dragon Ball, Digimon & more!
          </p>
        </div>
      </div>

      <div className="filter-tabs">
        {filterOptions.map((tab) => (
          <button
            key={tab.id}
            className={`filter-tab ${filter === tab.id ? 'active' : ''} ${hoveredFilter === tab.id ? 'hovered' : ''}`}
            onClick={() => setFilter(tab.id)}
            onMouseEnter={() => setHoveredFilter(tab.id)}
            onMouseLeave={() => setHoveredFilter(null)}
          >
            <span className="tab-text">{tab.label}</span>
            <span className="tab-burst">!</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading legendary cards...</p>
        </div>
      )}

      {error && !loading && (
        <div className="error-notice">
          <p>{error}</p>
        </div>
      )}

      {!loading && totalCards === 0 && (
        <div className="no-cards-message">
          <p>No cards available for this filter.</p>
        </div>
      )}

      {!loading && totalCards > 0 && (
        <div className="cards-grid">
          {pokemon.map((card) => (
            <Card key={`pokemon-${card.id}`} card={card} gameType="pokemon" />
          ))}
          {onePiece.map((card, index) => (
            <Card key={`onepiece-${card.id || index}`} card={card} gameType="one-piece" />
          ))}
          {dragonBall.map((card, index) => (
            <Card key={`dragonball-${card.id || index}`} card={card} gameType="dragon-ball" />
          ))}
          {digimon.map((card, index) => (
            <Card key={`digimon-${card.id || index}`} card={card} gameType="digimon" />
          ))}
        </div>
      )}

      <div className="gallery-decoration">
        <div className="speed-lines left"></div>
        <div className="speed-lines right"></div>
        <div className="halftone-dots"></div>
      </div>
    </section>
  );
};

export default CardGallery;
